// The workspace top bar's own words: the chat states, the places no other namespace names (Inbox, Agents,
// Connections), the phone menu button and the skip link before it. Namespace `workspace-topbar`. The other places
// keep the words they already show (ui/workspace/topbar-copy.ts); Inbox and Connections are the words the inbox and
// app-operations namespaces use.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  openingChat: 'Opening chat',
  chatUnavailable: 'Chat unavailable',
  inbox: 'Inbox',
  agents: 'Agents',
  projects: 'Projects',
  connections: 'Connections',
  toggleNavigation: 'Toggle navigation',
  skipToContent: 'Skip to content',
} as const;

type Copy = typeof en;
export const workspaceTopbarCopy: NamespaceCatalog<keyof Copy> = {
  bg: {
    openingChat: 'Отваряне на чата',
    chatUnavailable: 'Чатът не е достъпен',
    inbox: 'Входящи',
    agents: 'Агенти',
    projects: 'Проекти',
    connections: 'Връзки',
    toggleNavigation: 'Показване или скриване на навигацията',
    skipToContent: 'Към съдържанието',
  },
  hr: {
    openingChat: 'Otvaranje chata',
    chatUnavailable: 'Chat nije dostupan',
    inbox: 'Pristigla pošta',
    agents: 'Agenti',
    projects: 'Projekti',
    connections: 'Veze',
    toggleNavigation: 'Prikaži ili sakrij navigaciju',
    skipToContent: 'Preskoči na sadržaj',
  },
  cs: {
    openingChat: 'Otevírání chatu',
    chatUnavailable: 'Chat není k dispozici',
    inbox: 'Doručená pošta',
    agents: 'Agenti',
    projects: 'Projekty',
    connections: 'Připojení',
    toggleNavigation: 'Zobrazit nebo skrýt navigaci',
    skipToContent: 'Přeskočit na obsah',
  },
  da: {
    openingChat: 'Åbner chat',
    chatUnavailable: 'Chatten er ikke tilgængelig',
    inbox: 'Indbakke',
    agents: 'Agenter',
    projects: 'Projekter',
    connections: 'Forbindelser',
    toggleNavigation: 'Vis eller skjul navigation',
    skipToContent: 'Gå til indholdet',
  },
  nl: {
    openingChat: 'Chat openen',
    chatUnavailable: 'Chat niet beschikbaar',
    inbox: 'Inbox',
    agents: 'Agenten',
    projects: 'Projecten',
    connections: 'Verbindingen',
    toggleNavigation: 'Navigatie tonen of verbergen',
    skipToContent: 'Naar inhoud gaan',
  },
  en,
  et: {
    openingChat: 'Vestluse avamine',
    chatUnavailable: 'Vestlus pole saadaval',
    inbox: 'Postkast',
    agents: 'Agendid',
    projects: 'Projektid',
    connections: 'Ühendused',
    toggleNavigation: 'Näita või peida navigeerimine',
    skipToContent: 'Liigu sisu juurde',
  },
  fi: {
    openingChat: 'Avataan chattia',
    chatUnavailable: 'Chat ei ole saatavilla',
    inbox: 'Saapuneet',
    agents: 'Agentit',
    projects: 'Projektit',
    connections: 'Yhteydet',
    toggleNavigation: 'Näytä tai piilota navigointi',
    skipToContent: 'Siirry sisältöön',
  },
  fr: {
    openingChat: 'Ouverture de la discussion',
    chatUnavailable: 'Discussion indisponible',
    inbox: 'Boîte de réception',
    agents: 'Agents',
    projects: 'Projets',
    connections: 'Connexions',
    toggleNavigation: 'Afficher ou masquer la navigation',
    skipToContent: 'Aller au contenu',
  },
  de: {
    openingChat: 'Chat wird geöffnet',
    chatUnavailable: 'Chat nicht verfügbar',
    inbox: 'Posteingang',
    agents: 'Agenten',
    projects: 'Projekte',
    connections: 'Verbindungen',
    toggleNavigation: 'Navigation ein- oder ausblenden',
    skipToContent: 'Zum Inhalt springen',
  },
  el: {
    openingChat: 'Άνοιγμα συνομιλίας',
    chatUnavailable: 'Η συνομιλία δεν είναι διαθέσιμη',
    inbox: 'Εισερχόμενα',
    agents: 'Πράκτορες',
    projects: 'Έργα',
    connections: 'Συνδέσεις',
    toggleNavigation: 'Εμφάνιση ή απόκρυψη πλοήγησης',
    skipToContent: 'Μετάβαση στο περιεχόμενο',
  },
  hu: {
    openingChat: 'Chat megnyitása',
    chatUnavailable: 'A chat nem érhető el',
    inbox: 'Beérkezett elemek',
    agents: 'Ügynökök',
    projects: 'Projektek',
    connections: 'Kapcsolatok',
    toggleNavigation: 'Navigáció megjelenítése vagy elrejtése',
    skipToContent: 'Ugrás a tartalomhoz',
  },
  ga: {
    openingChat: 'Comhrá á oscailt',
    chatUnavailable: 'Níl an comhrá ar fáil',
    inbox: 'Bosca isteach',
    agents: 'Gníomhairí',
    projects: 'Tionscadail',
    connections: 'Ceangail',
    toggleNavigation: 'Taispeáin nó folaigh an nascleanúint',
    skipToContent: 'Léim go dtí an t-ábhar',
  },
  it: {
    openingChat: 'Apertura della chat',
    chatUnavailable: 'Chat non disponibile',
    inbox: 'Posta in arrivo',
    agents: 'Agenti',
    projects: 'Progetti',
    connections: 'Connessioni',
    toggleNavigation: 'Mostra o nascondi la navigazione',
    skipToContent: 'Vai al contenuto',
  },
  lv: {
    openingChat: 'Atver tērzēšanu',
    chatUnavailable: 'Tērzēšana nav pieejama',
    inbox: 'Iesūtne',
    agents: 'Aģenti',
    projects: 'Projekti',
    connections: 'Savienojumi',
    toggleNavigation: 'Rādīt vai paslēpt navigāciju',
    skipToContent: 'Pāriet uz saturu',
  },
  lt: {
    openingChat: 'Atidaromas pokalbis',
    chatUnavailable: 'Pokalbis nepasiekiamas',
    inbox: 'Gautieji',
    agents: 'Agentai',
    projects: 'Projektai',
    connections: 'Jungtys',
    toggleNavigation: 'Rodyti arba slėpti naršymą',
    skipToContent: 'Pereiti prie turinio',
  },
  mt: {
    openingChat: 'Qed jinfetaħ iċ-chat',
    chatUnavailable: 'Iċ-chat mhux disponibbli',
    inbox: 'Inbox',
    agents: 'Aġenti',
    projects: 'Proġetti',
    connections: 'Konnessjonijiet',
    toggleNavigation: 'Uri jew aħbi n-navigazzjoni',
    skipToContent: 'Aqbeż għall-kontenut',
  },
  pl: {
    openingChat: 'Otwieranie czatu',
    chatUnavailable: 'Czat niedostępny',
    inbox: 'Skrzynka odbiorcza',
    agents: 'Agenci',
    projects: 'Projekty',
    connections: 'Połączenia',
    toggleNavigation: 'Pokaż lub ukryj nawigację',
    skipToContent: 'Przejdź do treści',
  },
  pt: {
    openingChat: 'A abrir a conversa',
    chatUnavailable: 'Conversa indisponível',
    inbox: 'Caixa de entrada',
    agents: 'Agentes',
    projects: 'Projetos',
    connections: 'Ligações',
    toggleNavigation: 'Mostrar ou ocultar a navegação',
    skipToContent: 'Saltar para o conteúdo',
  },
  ro: {
    openingChat: 'Se deschide chatul',
    chatUnavailable: 'Chat indisponibil',
    inbox: 'Mesaje primite',
    agents: 'Agenți',
    projects: 'Proiecte',
    connections: 'Conexiuni',
    toggleNavigation: 'Afișează sau ascunde navigarea',
    skipToContent: 'Salt la conținut',
  },
  sk: {
    openingChat: 'Otvára sa chat',
    chatUnavailable: 'Chat nie je dostupný',
    inbox: 'Doručená pošta',
    agents: 'Agenti',
    projects: 'Projekty',
    connections: 'Pripojenia',
    toggleNavigation: 'Zobraziť alebo skryť navigáciu',
    skipToContent: 'Preskočiť na obsah',
  },
  sl: {
    openingChat: 'Odpiranje klepeta',
    chatUnavailable: 'Klepet ni na voljo',
    inbox: 'Prejeta pošta',
    agents: 'Agenti',
    projects: 'Projekti',
    connections: 'Povezave',
    toggleNavigation: 'Prikaži ali skrij navigacijo',
    skipToContent: 'Preskoči na vsebino',
  },
  es: {
    openingChat: 'Abriendo el chat',
    chatUnavailable: 'Chat no disponible',
    inbox: 'Bandeja de entrada',
    agents: 'Agentes',
    projects: 'Proyectos',
    connections: 'Conexiones',
    toggleNavigation: 'Mostrar u ocultar la navegación',
    skipToContent: 'Saltar al contenido',
  },
  sv: {
    openingChat: 'Öppnar chatten',
    chatUnavailable: 'Chatten är inte tillgänglig',
    inbox: 'Inkorg',
    agents: 'Agenter',
    projects: 'Projekt',
    connections: 'Anslutningar',
    toggleNavigation: 'Visa eller dölj navigeringen',
    skipToContent: 'Hoppa till innehållet',
  },
};
export default workspaceTopbarCopy;
